import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PageProps } from 'shared/defines';
import Lottie from 'lottie-react';
import animationTickGreen from '../shared/assets/lottie/animationTickGreen.json';
import { useTimeout } from 'usehooks-ts';
import { TextDecoder } from '../shared/text-effects/TextDecoder';
import { useTextSize } from '../shared/hooks/useTextSize';
import ChildWaiverContent from '../components/ChildWaiverContent';

const defaults = {
  spread: 250,
  ticks: 100,
  gravity: 0,
  decay: 0.94,
  startVelocity: 20,
};

type OrderCompletePageProps = PageProps & {
  includesShareWaiver?: boolean;
};

const OrderCompletePage: FC<OrderCompletePageProps> = ({
  title,
  includesShareWaiver,
}) => {
  const hiddenRef = useRef<HTMLHeadingElement>(null);
  const checkMarkRef = useRef<HTMLDivElement>(null);
  const { height: titleHeight } = useTextSize(hiddenRef);
  const [centerPosition, setCenterPosition] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  useTimeout(() => {
    if (checkMarkRef.current) {
      const elementRect = checkMarkRef.current.getBoundingClientRect();
      const centerX = elementRect.left + elementRect.width / 2;
      const centerY = elementRect.top + elementRect.height / 2;
      const x = centerX / window.innerWidth;
      const y = centerY / window.innerHeight;

      setCenterPosition({ x, y });
    }
  }, 100);

  useEffect(() => {
    document.title = title;
  }, [title]);

  const shoot = useCallback(() => {
    window.confetti({
      ...defaults,
      origin: { x: centerPosition.x, y: centerPosition.y },
      particleCount: 25,
      scalar: 1,
      shapes: ['circle'],
      colors: ['#1EEB78'],
    });
  }, [centerPosition]);

  useTimeout(shoot, 1500);
  useTimeout(shoot, 2500);
  useTimeout(shoot, 3500);

  return (
    <div className="w-full flex-1 flex flex-col sm:justify-center  gap-10">
      <div className="flex flex-col gap-2">
        <div
          className="whitespace-normal w-full max-w-[22.5rem] relative"
          style={{ height: `${titleHeight}px` }}
        >
          <TextDecoder
            className="font-aeonik font-medium text-[3.125rem]/[3.375rem] gap-5"
            delay={0}
            duration={500}
            animationType={'in'}
            text="Thank you for your order"
          />
          <div
            ref={hiddenRef}
            className="absolute opacity-0 top-0 pointer-events-none font-aeonik font-medium text-[3.125rem]/[3.375rem]"
          >
            Thank you for your order
          </div>
        </div>

        <div className="w-55 h-45 -ml-5" ref={checkMarkRef}>
          <Lottie
            loop={false}
            autoplay={true}
            animationData={animationTickGreen}
          />
        </div>
      </div>

      {includesShareWaiver && <ChildWaiverContent />}
    </div>
  );
};

export default OrderCompletePage;
